import { Button, DatePicker, Input, Select, Space, TableProps } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import LogViewModalKPI from "../../../../components/OwnKPI/LogViewModalKPI";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat } from "../../../../constants/constants";
import { useGetOwnKpiQuery } from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  GetOwnKpiParams,
  GetOwnKpiResponseType,
} from "../../../../types/performance";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";
import { planTypeColor, statusList } from "../OwnAppraisals/OwnAppraisals";
import { statusFilterList } from "../TeamMemberKPI/TeamMemberKPI";

export default function TeamMemberAppraisal() {
  const navigate = useNavigate();
  const { open, handleClose, handleOpen } = useModal<
    { log: any; kpi: string },
    string
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<GetOwnKpiParams>();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { isLoading, isFetching, isError, error, data } = useGetOwnKpiQuery({
    params: {
      ...filter,
      linemanager: authInfo?.id,
      title: keyword,
      positionStatus: "appraisal",
    },
  });
  useError(isError, error, "Get team appraisal fail");

  const columns: TableProps<
    GetOwnKpiResponseType["data"]["kpis"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      width: 300,
      render: (_, record) => record?.kpi?.kpi_title || "n/a",
    },
    {
      title: "Employee ID",
      width: 200,
      render: (_, record) => record?.employeeId || "n/a",
    },
    {
      title: "Employee Name",
      width: 200,
      render: (_, record) => record?.employee?.member?.profile?.name || "n/a",
    },
    {
      title: "Designation",
      width: 200,
      dataIndex: "designation",
      render: (val) => val || "n/a",
    },

    {
      title: "Plan Period",
      width: 200,
      dataIndex: "plan_period",
      render: (_, record) =>
        findPlanVal(planPeriodList, record?.kpi?.plan_period),
    },
    {
      title: "Plan Type",
      width: 200,
      dataIndex: "plan_type",
      render: (_, record) => {
        const type = record?.kpi?.plan_type as keyof typeof planTypeColor;
        return (
          <span
            style={planTypeColor[type] ? { color: planTypeColor[type] } : {}}
          >
            {findPlanVal(planTypeList, type)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "statusAppraisal",
      width: 350,
      render: (val) => {
        const st = statusList[val as keyof typeof statusList];
        return <span className={st?.style}>{st?.text}</span>;
      },
    },
    {
      title: "Due Date",
      width: 200,
      render: (_, record) => {
        const match = record?.kpi?.departments?.find(
          (dept: any) => dept.departmentId === record?.departmentId
        );

        return match ? formatDate(match.due_date) : "n/a";
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 200,
      render: (val) => {
        return val ? formatDate(val) : "n/a";
      },
    },
    {
      title: "Action",
      align: "center",
      dataIndex: "id",
      render: (val, record) => (
        <Space>
          <Link to={`/admin/kpi-setup/team-member-appraisal/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>

          <Button
            icon={<ActionWatchIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "OWN_KPI_LOG_VIEW",
                data: {
                  log: record?.actionLogs,
                  kpi: record?.kpi?.kpi_title,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="Team Member Appraisal List">
      {open.type === "OWN_KPI_LOG_VIEW" && (
        <LogViewModalKPI
          open={open.open}
          data={open?.data}
          handleClose={handleClose}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.kpis)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search...`}
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={handleSearch}
            />

            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("status", val)}
              placeholder="Status"
              filterOption={selectSearchOption}
              options={statusFilterList}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              dropdownStyle={{ minWidth: "230px" }}
            />

            <DatePicker
              format={dateFormat as unknown as string}
              style={{ width: 140 }}
              suffixIcon={<DateIcon />}
              className="clear-button-hide"
              // value={filter?.date ? dayjs(filter?.date) : null}
              // onChange={(date) => {
              //   const formattedDate = date?.format(dbDateFormat);
              //   handleFilter("date", formattedDate);
              // }}
            />
            {/* <Button
              type="primary"
              className="middle-button !p-4"
              icon={<SettingIcon />}
              onClick={() =>
                navigate(`/admin/kpi-setup/team-member-kpi/create`)
              }
            /> */}
          </>
        }
      />
    </SidebarLayout>
  );
}
